/**
 * RepBuddy - copy-to-clipboard with feedback (SCRUM-41)
 *
 * Copy buttons were scattered across the app with three different behaviours:
 * some gave no feedback at all, and some called navigator.clipboard with no
 * catch - which throws on an insecure origin (any non-localhost http:// host)
 * and left the user with a button that silently did nothing.
 *
 * This centralises the behaviour every copy button should have:
 *   - a clipboard fallback for browsers/origins without the async API
 *   - a transient "copied" state that reverts, so the button is always usable
 *     again immediately - copying twice is a normal thing to do
 *   - an explicit failure state, so a failed copy is never silent
 *   - timer cleanup, so a row unmounting mid-countdown cannot set state on an
 *     unmounted component
 */

import { useCallback, useEffect, useRef, useState } from 'react';

async function writeToClipboard(text) {
  // Preferred path - available on https:// and localhost.
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // Permission denied or insecure origin - fall through to the legacy path.
    }
  }

  // Legacy fallback: a hidden textarea plus execCommand.
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.top = '-9999px';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

/**
 * @param {number} resetMs how long the copied/failed state shows before reverting
 * @returns {{ status: 'idle'|'copied'|'failed', copy: (text: string) => Promise<boolean> }}
 */
export default function useCopyToClipboard(resetMs = 2000) {
  const [status, setStatus] = useState('idle');
  const timerRef = useRef(null);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  const copy = useCallback(async (text) => {
    const ok = await writeToClipboard(String(text ?? ''));

    // Restart the window on every click. Without this, clicking again while
    // "Copied" is still showing would inherit the first click's countdown and
    // flip back almost immediately, making the second copy look like it failed.
    clearTimeout(timerRef.current);
    setStatus(ok ? 'copied' : 'failed');
    timerRef.current = setTimeout(() => setStatus('idle'), resetMs);

    return ok;
  }, [resetMs]);

  return { status, copy };
}
